import React, { useState } from "react";
import { X, AlertCircle, CheckCircle, ChevronRight } from "lucide-react";
import { BASE_URL } from "../config";
import PhoneInput from "react-phone-input-2";
interface FormData {
  name: string;
  //propertyName: string;
  contact: string;
  email: string;
}

interface FormErrors {
  name?: string;
  //propertyName?: string;
  contact?: string;
  email?: string;
}

interface OwnerRegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (ownerData: any) => void;
}

export default function OwnerRegistrationModal({
  isOpen,
  onClose,
  onSuccess,
}: OwnerRegistrationModalProps) {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    //propertyName: '',
    contact: "",
    email: "",
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);


  // ───────── RESET ─────────
  const resetForm = () => {
    setFormData({ name: "", contact: "", email: "" });
    setErrors({});
    setShowSuccess(false);
  };

  // ───────── VALIDATION ─────────
  const validateField = (field: keyof FormData, value: string): string => {
    switch (field) {
      case "name":
        if (!value.trim()) return "Name is required";
        if (value.trim().length < 4) return "Minimum 4 characters";
        return "";

      // case 'propertyName':
      //     if (!value.trim()) return 'Property name is required';
      //     if (value.trim().length < 3) return 'Minimum 3 characters';
      //     return '';

      case "contact":
        if (!value.trim()) return "Phone number is required";

        // Country code ke saath 8–15 digits allow
        if (!/^\+?[1-9]\d{7,14}$/.test(value.trim())) {
          return "Enter a valid phone number with country code";
        }

        return "";

      case "email":
        if (value.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
          return "Invalid email format";
        }
        return "";

      default:
        return "";
    }
  };

  const validateForm = () => {
    const newErrors: FormErrors = {
      name: validateField("name", formData.name),
      //propertyName: validateField('propertyName', formData.propertyName),
      contact: validateField("contact", formData.contact),
      email: validateField("email", formData.email),
    };

    setErrors(newErrors);

    return !Object.values(newErrors).some((error) => error !== "");
  };

  // ───────── INPUT HANDLERS ─────────
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handlePhoneChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;

    setFormData((prev) => ({
      ...prev,
      contact: value,
    }));

    if (errors.contact) {
      setErrors((prev) => ({
        ...prev,
        contact: undefined,
      }));
    }
  };

  // ───────── SUBMIT ─────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);

    const payload = {
      name: formData.name,
      phone: formData.contact,
      email: formData.email,
      password: "",
    };

    console.log("[v0] Owner Registration Payload:", payload);

    try {
      const response = await fetch(`${BASE_URL}/owners/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      console.log("add owner detais resposn api == ", data);

      if (response.ok) {
        setShowSuccess(true);

        setTimeout(() => {
          onSuccess(data?.data);
          resetForm(); //  clear form after success
        }, 1000);
      } else {
        console.error("API Error:", data);
      }
    } catch (error) {
      console.error("Registration failed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-md">
        {/* HEADER */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-bold text-sage-900">Add Owner</h2>

          <button onClick={onClose} disabled={isLoading}>
            <X size={22} />
          </button>
        </div>

        {/* SUCCESS MESSAGE */}

        {showSuccess && (
          <div className="mx-6 mt-4 p-4 bg-green-50 border border-green-200 rounded-lg flex gap-2">
            <CheckCircle size={18} className="text-green-600" />
            <div className="text-sm text-green-700">
              Registration Successful. Opening next step...
            </div>
          </div>
        )}

        {/* FORM */}

        <form onSubmit={handleSubmit} className="p-6 space-y-4">

          {/* OWNER NAME */}

          <div>
            <label className="text-sm font-medium">Owner Name *</label>

            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="w-full border rounded-lg px-3 py-2 mt-1"
              placeholder="Enter your full name"
            />

            {errors.name && (
              <p className="text-red-500 text-xs mt-1">{errors.name}</p>
            )}
          </div>

          {/* PHONE */}

          {/* PHONE */}

          <div>
            <label className="text-sm font-medium">Phone Number *</label>

            <div className="mt-1">
              <PhoneInput
                country={"tz"}           // Default Tanzania
                enableSearch={true}      // Country search
                value={formData.contact}
                onChange={(phone) => {
                  setFormData((prev) => ({
                    ...prev,
                    contact: "+" + phone,
                  }));

                  if (errors.contact) {
                    setErrors((prev) => ({
                      ...prev,
                      contact: undefined,
                    }));
                  }
                }}
                inputClass="!w-full !h-11"
                buttonClass="!border-r"
                containerClass="!w-full"
                inputProps={{
                  name: "contact",
                  required: true,
                }}
              />
            </div>

            {errors.contact && (
              <p className="text-red-500 text-xs mt-1">
                {errors.contact}
              </p>
            )}
          </div>

          {/* EMAIL */}

          <div>
            <label className="text-sm font-medium">Email (Optional)</label>

            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className="w-full border rounded-lg px-3 py-2 mt-1"
              placeholder="Enter email"
            />

            {errors.email && (
              <p className="text-red-500 text-xs mt-1">{errors.email}</p>
            )}
          </div>

          {/* BUTTON */}
          <button
            type="submit"
            className="w-full bg-green-700 text-white py-3 rounded-lg"
          >
            Submit
          </button>
        </form>
      </div>
    </div>
  );
}
